import { useEffect, useState } from 'react';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@hooks/useTranslation';
import styles from './EditAndResendModal.module.scss';

export type RetriggerMode = 'reset_all' | 'continue';

interface EditAndResendModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called with the chosen mode when the user confirms. Caller is responsible
   * for navigation. Modal is only meaningful when the envelope has at least one
   * SIGNED recipient — callers MUST skip the modal entirely otherwise and
   * navigate directly with `reset_all`.
   */
  onConfirm: (mode: RetriggerMode) => void;
}

export function EditAndResendModal({ isOpen, onClose, onConfirm }: EditAndResendModalProps) {
  const { t } = useTranslation();
  const [retriggerMode, setRetriggerMode] = useState<RetriggerMode>('continue');

  // Reset to the safer default whenever the modal re-opens — never carry the
  // previous selection across two different envelopes.
  useEffect(() => {
    if (isOpen) setRetriggerMode('continue');
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('K_DS_MODAL_EDIT_AND_RESEND_TITLE', 'Edit & Resend Signing Request')}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button variant="filled" onClick={() => onConfirm(retriggerMode)}>
            {t('K_DS_BUTTON_CONTINUE_TO_EDIT', 'Continue to Edit')}
          </Button>
        </>
      }
    >
      <p className={styles.modalIntro}>
        {t(
          'K_DS_MODAL_EDIT_AND_RESEND_INTRO',
          'Choose how to handle existing signing progress after editing.',
        )}
      </p>

      <div className={styles.optionList}>
        <label
          className={`${styles.option} ${retriggerMode === 'continue' ? styles.optionSelected : ''}`}
        >
          <input
            type="radio"
            name="retriggerMode"
            value="continue"
            checked={retriggerMode === 'continue'}
            onChange={() => setRetriggerMode('continue')}
          />
          <div>
            <div className={styles.optionTitle}>
              {t('K_DS_OPTION_CONTINUE_SIGNING', 'Continue with remaining signers')}
            </div>
            <div className={styles.optionDesc}>
              {t(
                'K_DS_OPTION_CONTINUE_SIGNING_DESC',
                'Keep existing signatures. Only unsigned recipients will be resent.',
              )}
            </div>
          </div>
        </label>

        <label
          className={`${styles.option} ${retriggerMode === 'reset_all' ? styles.optionSelected : ''}`}
        >
          <input
            type="radio"
            name="retriggerMode"
            value="reset_all"
            checked={retriggerMode === 'reset_all'}
            onChange={() => setRetriggerMode('reset_all')}
          />
          <div>
            <div className={styles.optionTitle}>
              {t('K_DS_OPTION_RESET_ALL', 'Reset all and resend to everyone')}
            </div>
            <div className={styles.optionDesc}>
              {t(
                'K_DS_OPTION_RESET_ALL_DESC',
                'Discard all signatures. Every recipient will need to sign again.',
              )}
            </div>
          </div>
        </label>
      </div>
    </Modal>
  );
}

export default EditAndResendModal;
